import React, { useMemo } from 'react';
import { Flex, GridItem, Text, Tooltip } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { IGame } from '../../../../providers/Games/useGames';
import { useGameColorsProvider } from '../../../../providers/Theme/gameColorsProvider';
import { contrastColors, trimAddress } from 'src/utils/parser';
import { Color } from 'src/theme/color';
import { getTransparent } from '../../../../utils/themer';

interface IContent {
	content: {
		user?: { address: string; name?: string };
		game?: { address: string; name?: string; amount: string };
		generic?: string;
	};
	customTheme?: /** @TODO: redundant; remove this */ IGame['colors'];
}

enum ContentType {
	USER,
	GAME,
	GENERIC,
}

export const Content = ({ content, customTheme }: IContent) => {
	const history = useNavigate();
	const { callToActionTextColor, callToActionColor } = useGameColorsProvider();

	const navigateUser = () => {
		history(`/user/${content.user?.address}`);
	};

	const navigateGame = () => {
		history(`/game/${content.game?.address}`);
	};

	const typeOptions: { [p: number]: () => React.ReactNode } = {
		[ContentType.USER]: () =>
			content.user && (
				<Flex
					gridGap="8px"
					cursor="pointer"
					onClick={navigateUser}
				>
					{content.user.name && (
						<Text
							fontWeight={customTheme ? '600' : 'unset'}
							color={customTheme?.textColor ?? 'text'}
							maxWidth="7em"
							textOverflow="ellipsis"
							whiteSpace="nowrap"
							overflow="hidden"
						>
							{content.user.name}
						</Text>
					)}
					<Text color={getTransparent(Color.TEXT, 50)}>{trimAddress(content.user.address)}</Text>
				</Flex>
			),
		[ContentType.GAME]: () =>
			content.game && (
				<Flex
					gridGap="8px"
					cursor="pointer"
					onClick={navigateGame}
					alignItems="baseline"
				>
					{content.game.name && (
						<Tooltip
							label={content.game.name}
							placement="bottom-end"
						>
							<Text
								fontWeight={customTheme ? '600' : 'unset'}
								color={customTheme?.textColor ?? 'text'}
								maxWidth="7em"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								overflow="hidden"
							>
								{content.game.name}
							</Text>
						</Tooltip>
					)}
					{content.game.amount !== '100' && (
						<Text
							fontWeight="700"
							marginLeft="-4px"
							bg={customTheme ? callToActionColor : Color.ACTION}
							color={
								customTheme
									? callToActionTextColor
									: contrastColors(Color.ACTION, '#ffffff') > 3.1
									? 'text'
									: 'textSecondary'
							}
							padding="1px 4px"
							borderRadius="4px"
						>
							x{Number(ethers.utils.formatUnits(content.game.amount, 2))}
						</Text>
					)}
					<Text color={getTransparent(Color.TEXT, 50)}>{trimAddress(content.game.address)}</Text>
				</Flex>
			),
		[ContentType.GENERIC]: () => (
			<Text
				fontSize="10px"
				lineHeight="14px"
				color={customTheme?.textColor ?? 'text'}
			>
				{content.generic}
			</Text>
		),
	};

	const connectType = useMemo(() => {
		if (content.user) {
			return ContentType.USER;
		}
		if (content.game) {
			return ContentType.GAME;
		}
		if (content.generic) {
			return ContentType.GENERIC;
		}
		return ContentType.USER;
	}, [content]);

	return <GridItem>{typeOptions[connectType]()}</GridItem>;
};
